import * as THREE from 'three';
import { mat } from '../world/palette.js';
import { patrolPath } from './characters-paths.js';

export type CharacterRole = 'staff' | 'client';

export interface CharacterAgent {
  mesh: THREE.Object3D;
  role: CharacterRole;
  path: THREE.Vector3[];
  pathIndex: number;
  speed: number;
  phase: number;
  mixer?: THREE.AnimationMixer;
}

const STAFF_COLORS = [0x2c5282, 0x234e52, 0x744210];
const CLIENT_COLORS = [0xc05621, 0x6b46c1, 0x2f855a, 0xb83280, 0x4a5568];

function buildHumanoid(color: number, role: CharacterRole): THREE.Group {
  const g = new THREE.Group();
  const body = new THREE.Mesh(new THREE.CapsuleGeometry(0.22, 0.55, 4, 8), mat(color));
  body.position.y = 0.95;
  body.castShadow = true;
  const head = new THREE.Mesh(new THREE.SphereGeometry(0.2, 12, 10), mat(0xffdbac));
  head.position.y = 1.45;
  head.castShadow = true;
  if (role === 'staff') {
    const apron = new THREE.Mesh(new THREE.BoxGeometry(0.5, 0.45, 0.12), mat(0xffffff));
    apron.position.set(0, 0.85, 0.14);
    g.add(apron);
  }
  g.add(body, head);
  return g;
}

/** Placeholders hasta que carguen los GLTF animados. */
export function spawnPlaceholderCharacters(
  root: THREE.Group,
  countStaff: number,
  countClients: number,
): CharacterAgent[] {
  const agents: CharacterAgent[] = [];
  for (let i = 0; i < countStaff; i += 1) {
    const mesh = buildHumanoid(STAFF_COLORS[i % STAFF_COLORS.length], 'staff');
    mesh.position.set(-6 + i * 2.5, 0, -8 + (i % 2) * 3);
    root.add(mesh);
    agents.push({
      mesh,
      role: 'staff',
      path: patrolPath('staff', i),
      pathIndex: 0,
      speed: 0.8 + (i % 3) * 0.15,
      phase: Math.random() * Math.PI * 2,
    });
  }
  for (let i = 0; i < countClients; i += 1) {
    const mesh = buildHumanoid(CLIENT_COLORS[i % CLIENT_COLORS.length], 'client');
    mesh.position.set(-10 + (i % 4) * 3, 0, 2 + Math.floor(i / 4) * 2);
    root.add(mesh);
    agents.push({
      mesh,
      role: 'client',
      path: patrolPath('client', i),
      pathIndex: 0,
      speed: 0.55 + (i % 4) * 0.1,
      phase: Math.random() * Math.PI * 2,
    });
  }
  return agents;
}

export function tickCharacters(agents: CharacterAgent[], dt: number, t: number): void {
  for (const agent of agents) {
    agent.mixer?.update(dt);
    const target = agent.path[agent.pathIndex];
    const pos = agent.mesh.position;
    const dx = target.x - pos.x;
    const dz = target.z - pos.z;
    const dist = Math.hypot(dx, dz);
    if (dist < 0.2) {
      agent.pathIndex = (agent.pathIndex + 1) % agent.path.length;
    } else {
      const step = agent.speed * dt;
      pos.x += (dx / dist) * step;
      pos.z += (dz / dist) * step;
      agent.mesh.rotation.y = Math.atan2(dx, dz);
    }
    const moving = dist > 0.25;
    const walkPhase = (agent.mesh.userData.walkPhase as number) ?? agent.phase;
    if (moving) {
      agent.mesh.userData.walkPhase = walkPhase + dt * 9;
      const swing = Math.sin((agent.mesh.userData.walkPhase as number)) * 0.42;
      agent.mesh.traverse((c) => {
        if (c.userData.leg === 'L') c.rotation.x = swing;
        if (c.userData.leg === 'R') c.rotation.x = -swing;
      });
      agent.mesh.position.y = Math.abs(Math.sin((agent.mesh.userData.walkPhase as number) * 2)) * 0.04;
    } else if (!agent.mixer) {
      agent.mesh.position.y = Math.sin(t * 4 + agent.phase) * 0.02;
    }
  }
}
